import React, { useEffect, useState } from "react";
import {
  Form,
  Dropdown,
  DropdownButton,
  Container,
  Row,
  Col,
  Card,
  InputGroup,
  Badge,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  FaBlog,
  FaYoutube,
  FaEnvelope,
  FaPodcast,
  FaTiktok,
  FaInstagram,
  FaBullhorn,
  FaLinkedin,
  FaHeading,
} from "react-icons/fa";
import { socialHeadlineAiResponse } from "../../services/getAiResponse";
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";
import { Box } from "./SocialHeadlineStyle";

import {
  validateHookTopic,
  validateTargetAudience,
  isMeaningfulText,
} from "../../utils/validators";

const SocialHeadlinesGenerator: React.FC = () => {
  const [topic, setTopic] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("Blog");
  const [headlines, setHeadlines] = useState<string[]>([]);
  const [analysis, setAnalysis] = useState<string>("");
  const [relatedTopics, setRelatedTopics] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [audience, setAudience] = useState("");
  const [tone, setTone] = useState("Professional");
  const [error, setError] = useState<string>(""); 

  const [topicError, setTopicError] = useState<string | null>(null);
  const [audienceError, setAudienceError] = useState<string | null>(null);

  const platforms = [
    { name: "Blog", icon: <FaBlog size={18} style={{ marginRight: "5px" }} color="#6f42c1" /> },
    { name: "YouTube", icon: <FaYoutube size={18} style={{ marginRight: "5px" }} color="#FF0000" /> },
    { name: "Email", icon: <FaEnvelope size={18} style={{ marginRight: "5px" }} color="#007bff" /> },
    { name: "Podcast", icon: <FaPodcast size={18} style={{ marginRight: "5px" }} color="#ff6600" /> },
    { name: "TikTok", icon: <FaTiktok size={18} style={{ marginRight: "5px" }} color="#000000" /> },
    { name: "Instagram", icon: <FaInstagram size={18} style={{ marginRight: "5px" }} color="#E1306C" /> },
    { name: "Ad", icon: <FaBullhorn size={18} style={{ marginRight: "5px" }} color="#ffc107" /> },
    { name: "LinkedIn", icon: <FaLinkedin size={18} style={{ marginRight: "5px" }} color="#0077b5" /> },
    { name: "Headline", icon: <FaHeading size={18} style={{ marginRight: "5px" }} color="#17a2b8" /> },
  ];

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [headlines, analysis, relatedTopics, topicError, audienceError, error]);

  const generateHeadlines = async () => {
    const topicErr = validateHookTopic(topic);
    setTopicError(topicErr);

    let audienceErr: string | null = null;
    if (audience.trim()) {
      audienceErr = validateTargetAudience(audience);
      if (!audienceErr && !isMeaningfulText(audience)) {
        audienceErr = "Please describe the audience more clearly";
      }
    }
    setAudienceError(audienceErr);

    if (topicErr) {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
      return;
    }

    setError("");

    setLoading(true);

    try {
      const { headlines, analysis, relatedTopics } = await socialHeadlineAiResponse(
        topic,
        selectedPlatform,
        audience,
        tone
      );
      setHeadlines(headlines);
      setAnalysis(analysis);
      setRelatedTopics(relatedTopics);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (err) {
      console.error("Error:", err);
      setError("Failed to generate headlines. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={`${!isInIframe ? 'py-5 defaultText' : 'py-1 interDisplay'}`} style={{ padding: "0px" }}>
      <Container className={`py-4 ${!isInIframe ? "mt-5" : "py-2"}`} fluid="md" style={{ padding: "0px" }}>
        {!isInIframe && (
          <ToolsHeader
            title="Social Headlines Generator"
            description="Create engaging headlines for your social media platforms with ease."
          />
        )}

        <Alert variant="danger" show={!!error} onClose={() => setError("")} dismissible>
          {error}
        </Alert>

        <Card className="p-4 mb-4 shadow-sm animate-card">
          <Row className="g-3">
            <Col xs={12}>
              <Form.Group controlId="topicInput">
                <Form.Label className="fw-semibold">Topic or Keywords</Form.Label>
                <InputGroup className="input-group-nowrap">
                  <Form.Control
                    type="text"
                    placeholder="Enter topic or keywords..."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    isInvalid={!!topicError}
                    aria-describedby="topicHelp"
                    className="topic-input"
                  />
                  <DropdownButton
                    variant="outline-primary"
                    title={
                      <span className="align-items-center gap-2">
                        {platforms.find((p) => p.name === selectedPlatform)?.icon}
                        {selectedPlatform}
                      </span>
                    }
                    align="end"
                    className="platform-dropdown"
                  >
                    {platforms.map((platform) => (
                      <Dropdown.Item
                        key={platform.name}
                        onClick={() => setSelectedPlatform(platform.name)}
                        className="d-flex align-items-center gap-2"
                      >
                        {platform.icon} {platform.name}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                </InputGroup>

                {topicError && (
                  <div className="invalid-feedback d-block mt-1">
                    {topicError}
                  </div>
                )}

                <Form.Text id="topicHelp" muted className="mt-1">
                  Enter a topic or keywords to generate tailored headlines.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group controlId="audienceInput">
                <Form.Label className="fw-semibold">Target Audience</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Small business owners, Tech enthusiasts"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  isInvalid={!!audienceError}
                />

                {audienceError && (
                  <div className="invalid-feedback d-block mt-1">
                    {audienceError}
                  </div>
                )}

                <Form.Text muted className="mt-1">
                  Optional: Specify your target audience.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group controlId="toneInput">
                <Form.Label className="fw-semibold">Tone of Voice</Form.Label>
                <Form.Select value={tone} onChange={(e) => setTone(e.target.value)}>
                  <option value="Professional">Professional</option>
                  <option value="Casual">Casual</option>
                  <option value="Witty">Witty</option>
                  <option value="Inspirational">Inspirational</option>
                  <option value="Bold">Bold</option>
                </Form.Select>
                <Form.Text muted className="mt-1">
                  Optional: Choose the tone for your headlines.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col xs={12} className="text-end">
              <Button
                variant="primary"
                onClick={generateHeadlines}
                disabled={loading}
                className="generate-button"
                aria-label="Generate headlines"
              >
                {loading ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <span className="d-flex align-items-center gap-2">
                    ✨ Generate Headlines
                  </span>
                )}
              </Button>
            </Col>
          </Row>
        </Card>

        {headlines.length > 0 && (
          <Card className="p-4 mb-4 shadow-sm animate-card">
            <h5 className="fw-semibold mb-3">📢 Generated Headlines</h5>
            {headlines.map((headline, index) => (
              <Card.Text key={index} className="py-2 border-bottom">
                {headline}
              </Card.Text>
            ))}
          </Card>
        )}

        {analysis && (
          <Card className="p-4 mb-4 shadow-sm animate-card">
            <h5 className="fw-semibold mb-3">📊 Analysis</h5>
            <p>{analysis}</p>
          </Card>
        )}

        {relatedTopics.length > 0 && (
          <Card className="p-4 shadow-sm animate-card">
            <h5 className="fw-semibold mb-3">🔗 Related Topics</h5>
            <div className="d-flex flex-wrap gap-2">
              {relatedTopics.map((topic, index) => (
                <Badge key={index} pill className="px-3 py-2">
                  {topic}
                </Badge>
              ))}
            </div>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default SocialHeadlinesGenerator;